import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { mediaUrl, kr } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { ProductCarousel } from "@/components/ProductCarousel";
import { SlideShow } from "@/components/SlideShow";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { E, EditableProvider } from "@/components/Editable";
import { useDevice, dv, resolveBlock, isBlockVisible, useViewportWidth, REF_W } from "@/components/Device";
import { CategoryIcon } from "@/components/CategoryIcon";
import { PARTS } from "@/components/blockParts";

export const LABELS = { heading: "Overskrift", subheading: "Underoverskrift", text: "Brødtekst", button: "Knap", image: "Billede", video: "Video", banner: "Banner", hero: "Hero", divider: "Divider", spacer: "Spacer", section: "Sektion", product_grid: "Produktgrid", product_carousel: "Produktkarussel", offers_grid: "Tilbudsprodukter", category_grid: "Kategorigrid", collections: "Udvalgte kolleksioner", slide: "Slide", header: "Header", footer: "Footer", campaigns: "Kampagner / Dagens 1 MERE" };
// Every storefront section must exist as an editable block; auto-insert the campaigns strip after the header.
export const ensureCampaigns = (blocks) => (blocks.some((b) => b.type === "campaigns") ? blocks : (() => { const n = [...blocks]; const i = n.findIndex((b) => b.type === "header"); n.splice(i >= 0 ? i + 1 : 0, 0, { id: "campaigns", type: "campaigns", styles: { padding: 0 }, content: {}, data: {}, visibility: { desktop: true, tablet: true, mobile: true } }); return n; })());
// Renders a single layout block. Used by both the storefront and the editor canvas.
export function BlockRenderer({ block, editor = false, elProps = {}, nested = false, prefix = "" }) {
  const eid = (k) => prefix + k;
  const navigate = useNavigate();
  const device = useDevice();
  const vw = useViewportWidth();
  const isPc = device === "desktop";
  const s = block.styles || {};
  const c = block.content || {};
  const d = block.data || {};
  const [items, setItems] = useState([]);
  const [slide, setSlide] = useState(null);
  const [cats, setCats] = useState([]);

  const needsProducts = ["product_grid", "product_carousel", "offers_grid"].includes(block.type);
  useEffect(() => {
    if (needsProducts) {
      const q = new URLSearchParams();
      q.set("limit", String(d.limit || 12));
      if (block.type === "offers_grid") q.set("sale", "true");
      if (d.category) q.set("category", d.category);
      if (d.sort) q.set("sort", d.sort);
      api.get(`/products?${q}`).then(({ data }) => setItems(data.products || [])).catch(() => {});
    }
    if (block.type === "category_grid" || block.type === "collections") api.get("/categories?visible_only=true").then(({ data }) => setCats(data.filter((x) => !x.parent_id))).catch(() => {});
    if (block.type === "slide" && d.slideId) api.get("/slides?include_inactive=true").then(({ data }) => setSlide((data || []).find((x) => x.id === d.slideId) || null)).catch(() => {});
  }, [block.type, d.limit, d.category, d.sort, d.slideId, needsProducts]);

  const wrap = {
    padding: s.padding != null ? `${s.padding}px` : undefined,
    background: s.bg || undefined,
    color: s.color || undefined,
    borderRadius: s.radius != null ? `${s.radius}px` : undefined,
    textAlign: s.align || undefined,
  };
  const inner = { maxWidth: s.width ? `${s.width}%` : "100%", marginLeft: s.align === "center" ? "auto" : undefined, marginRight: s.align === "center" || s.align === "right" ? "auto" : undefined, width: "100%" };
  const ts = { fontFamily: s.font || undefined, letterSpacing: s.letterSpacing != null ? s.letterSpacing + "px" : undefined };
  const goLink = () => { if (!editor && block.link) { if (/^https?:/.test(block.link)) window.location.href = block.link; else navigate(block.link); } };

  const content = () => {
    const part = PARTS[block.type];
    return part ? part({ s, c, d, ts, eid, device, isPc, editor, goLink, navigate, items, cats, slide, block }) : <div className="text-sm text-muted-foreground">Ukendt element: {block.type}</div>;
  };

  const isFull = ["section", "header", "footer", "campaigns"].includes(block.type);
  const kids = (block.children || []).map((ch) => (
    <E key={ch.id} id={`c${ch.id}-`} label={`${LABELS[ch.type] || ch.type} (element)`} kind={["image", "video"].includes(ch.type) ? "image" : "box"} display="block">
      {React.createElement(BlockRenderer, { block: ch, editor, nested: true, prefix: `c${ch.id}-` })}
    </E>
  ));
  if (nested) return <div style={wrap} className="w-full"><div style={inner} className="relative">{content()}{kids}</div></div>;
  return (
    <EditableProvider blockId={block.id} elements={block.elements || {}} editor={editor} {...elProps}>
      <div style={wrap} className={isFull ? "w-full" : "w-full px-4"}>
        <div style={inner} className="relative mx-auto max-w-7xl">{content()}{kids}</div>
      </div>
    </EditableProvider>
  );
}

export function BlockList({ blocks = [] }) {
  const device = useDevice();
  const vw = useViewportWidth();
  const authored = blocks.find((b) => b.ref_w && Object.keys(b.elements || {}).length)?.ref_w;
  const zoom = authored ? Math.min(1.15, vw / (device === "desktop" ? authored : REF_W[device])) : undefined;
  return (
    <div style={zoom ? { zoom } : undefined}>
      {ensureCampaigns(blocks).filter((b) => isBlockVisible(b, device)).map((b) => <BlockRenderer key={b.id} block={resolveBlock(b, device)} />)}
    </div>
  );
}
